import { useMemo } from "react";
import {
  Box,
  Divider,
  IconButton,
  MenuItem,
  ListItemIcon,
  Skeleton,
  Theme,
  Typography,
  styled,
  useMediaQuery,
  ListItemText,
} from "@mui/material";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { ActionMenu } from "@components";
import { useActionMenu } from "@hooks";
import { RequestDetail } from "../../../interfaces";
import { getUnitForQuantity } from "../../../utils";

const Container = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  border: "1px solid #eaeaea",
  borderRadius: "8px",
  backgroundColor: theme.palette.background.paper,
  gap: "4px",
  padding: "8px 16px",
}));

const Row = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  gap: "16px",
  alignItems: "flex-start",
});

const InfoColumn = styled(Box)({
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minWidth: 0,
  gap: "4px",
});

const ActionColumn = styled(Box)({
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "flex-start",
  flexShrink: 0,
});

const ProductName = styled(Typography)(({ theme }) => ({
  fontWeight: 500,
  color: theme.palette.text.primary,
  textWrap: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
}));

const ProductDetails = styled(Typography)(({ theme }) => ({
  fontSize: "14px",
  color: theme.palette.text.secondary,
}));

const TreatmentDetail = styled(Typography)(({ theme }) => ({
  fontSize: "14px",
  color: theme.palette.text.secondary,
}));

const ActionButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.icon.primary,
  padding: 0,
}));

const ListItemIconStyled = styled(ListItemIcon)(({ theme }) => ({
  color: theme.palette.icon.primary,
}));

export type PrescriptionCard = Pick<
  RequestDetail,
  | "name"
  | "activePrinciple"
  | "concentration"
  | "laboratory"
  | "posologyQuantity"
  | "posologyFormat"
  | "frequencyQuantity"
  | "frequencyPeriod"
  | "durationQuantity"
  | "durationPeriod"
  | "quantityToDispense"
  | "indications"
>;

interface PrescriptionDetailCardProps {
  detail: PrescriptionCard;
  handleDelete?: (detail: PrescriptionCard) => void;
  handleEdit?: (detail: PrescriptionCard) => void;
}

export function PrescriptionDetailCard(props: PrescriptionDetailCardProps) {
  const { detail, handleDelete, handleEdit } = props;
  const {
    activePrinciple,
    concentration,
    name,
    laboratory,
    posologyQuantity,
    posologyFormat,
    frequencyQuantity,
    frequencyPeriod,
    durationQuantity,
    durationPeriod,
    quantityToDispense,
    indications,
  } = detail;

  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md"),
  );
  const actionMenu = useActionMenu();
  const cardTitle = useMemo(() => {
    const hasLessThan4ActivePrinciples =
      activePrinciple && activePrinciple.split(";").length < 4;
    const hasLessThan4Concentrations =
      concentration && concentration.split(";").length < 4;
    if (hasLessThan4ActivePrinciples || hasLessThan4Concentrations) {
      return `${activePrinciple ?? ""} ${concentration ?? ""}`.trim();
    } else {
      return name;
    }
  }, [name, activePrinciple, concentration]);

  const cardSubTitle = useMemo(() => {
    const parsedName =
      name && activePrinciple && name !== activePrinciple && name !== cardTitle
        ? name
        : null;
    const parsedLaboratory =
      laboratory && laboratory !== "TO_DO" ? laboratory : null;
    return [parsedName, parsedLaboratory].filter(Boolean).join(" · ");
  }, [name, activePrinciple, laboratory, cardTitle]);

  const consumptionDetail = useMemo(() => {
    if (posologyQuantity && posologyFormat) {
      return `${posologyQuantity} ${getUnitForQuantity(
        posologyFormat,
        posologyQuantity,
      )} cada ${frequencyQuantity} ${getUnitForQuantity(
        frequencyPeriod,
        frequencyQuantity,
      )}`;
    }
    return "";
  }, [posologyQuantity, posologyFormat, frequencyQuantity, frequencyPeriod]);

  const treatmentDuration = useMemo(() => {
    if (durationQuantity && durationPeriod) {
      return `Tratamiento por ${durationQuantity} ${getUnitForQuantity(
        durationPeriod,
        durationQuantity,
      )}`;
    }
    return "";
  }, [durationQuantity, durationPeriod]);

  const isTabletOrCapsule = useMemo(() => {
    return posologyFormat === "comprimido" || posologyFormat === "cápsula";
  }, [posologyFormat]);

  const quantityToDispenseText = useMemo(() => {
    if (quantityToDispense) {
      return `Cant. a dispensar: ${quantityToDispense} ${
        isTabletOrCapsule
          ? getUnitForQuantity("caja", quantityToDispense)
          : getUnitForQuantity("unidad", quantityToDispense)
      }`;
    }
    return "";
  }, [isTabletOrCapsule, quantityToDispense]);

  const hasActions = Boolean(handleEdit || handleDelete);

  return (
    <Container>
      <Row>
        <InfoColumn>
          {cardTitle && <ProductName>{cardTitle}</ProductName>}
          {cardSubTitle && <ProductDetails>{cardSubTitle}</ProductDetails>}
          {consumptionDetail && (
            <TreatmentDetail>{consumptionDetail}</TreatmentDetail>
          )}
          {treatmentDuration && (
            <TreatmentDetail>{treatmentDuration}</TreatmentDetail>
          )}
          {quantityToDispenseText && (
            <TreatmentDetail>{quantityToDispenseText}</TreatmentDetail>
          )}
        </InfoColumn>
        {isMobile ? (
          hasActions && (
            <ActionColumn>
              <ActionButton
                aria-label={`Más acciones para ${cardTitle}`}
                onClick={actionMenu.open}
              >
                <MoreVertIcon />
              </ActionButton>
              <ActionMenu
                anchorEl={actionMenu.anchorEl}
                open={actionMenu.isOpen}
                onClose={actionMenu.close}
              >
                {handleEdit && (
                  <MenuItem
                    disableRipple
                    onClick={() => {
                      handleEdit(detail);
                      actionMenu.close();
                    }}
                  >
                    <ListItemIconStyled>
                      <EditOutlinedIcon fontSize="small" />
                    </ListItemIconStyled>
                    <ListItemText>Editar</ListItemText>
                  </MenuItem>
                )}
                {handleDelete && (
                  <MenuItem
                    disableRipple
                    onClick={() => {
                      handleDelete(detail);
                      actionMenu.close();
                    }}
                  >
                    <ListItemIconStyled>
                      <DeleteOutlinedIcon fontSize="small" />
                    </ListItemIconStyled>
                    <ListItemText>Eliminar</ListItemText>
                  </MenuItem>
                )}
              </ActionMenu>
            </ActionColumn>
          )
        ) : (
          <>
            {handleEdit && (
              <ActionColumn>
                <ActionButton
                  aria-label={`Editar ${cardTitle}`}
                  onClick={() => handleEdit(detail)}
                >
                  <EditOutlinedIcon />
                </ActionButton>
              </ActionColumn>
            )}
            {handleDelete && (
              <ActionColumn>
                <ActionButton
                  aria-label={`Eliminar ${cardTitle}`}
                  onClick={() => handleDelete(detail)}
                >
                  <DeleteOutlinedIcon />
                </ActionButton>
              </ActionColumn>
            )}
          </>
        )}
      </Row>
      {indications && (
        <>
          <Divider />
          <TreatmentDetail>{indications}</TreatmentDetail>
        </>
      )}
    </Container>
  );
}

PrescriptionDetailCard.Skeleton = function PrescriptionDetailCardSkeleton() {
  return (
    <Container>
      <Row>
        <InfoColumn>
          <ProductName>
            <Skeleton variant="text" width="90%" />
          </ProductName>
          <ProductDetails>
            <Skeleton variant="text" width="90%" />
          </ProductDetails>
          <TreatmentDetail>
            <Skeleton variant="text" width="90%" />
          </TreatmentDetail>
        </InfoColumn>
        <ActionColumn></ActionColumn>
      </Row>
    </Container>
  );
};
